import { useIntl } from 'react-intl';

import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { convertToTimeseriesArray } from './utils/convert-to-timeseries-array.js';
import { getSelectedDatapoints } from './utils/get-selected-datapoints.js';
import { isDatapointWithinSelection } from './utils/is-datapoint-within-selection.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { getCustomContextMenuItems } from '../../../../core/components/interactions-overlay/utils/get-custom-context-menu-items.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { SelectionTooltipWithAsyncActions } from '../../../../core/components/selection-area/SelectionTooltipWithAsyncActions.js';
import { getSelectionTooltipPosition } from '../../../../core/components/selection-area/tooltip/get-selection-tooltip-position.js';
import { usePointerAndScrollResponsiveChartBounds } from '../../../../core/hooks/usePointerAndScrollResponsiveChartBounds.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useZoomToSelection } from '../../../hooks/store/state-setters.js';
import { useChartInteractions } from '../../../hooks/useChartInteractions.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useTimeseriesOverlayRef } from '../../../hooks/useTimeseriesOverlayRef.js';
import { useTimeseriesSelect } from '../../../hooks/useTimeseriesSelect.js';
import { useXScales } from '../../../hooks/useXScale.js';
import { TIMESERIES_MESSAGES } from '../../../messages/tooltips-text.js';
import { getDatapointsMessage } from '../../../utils/get-datapoints-message.js';
import { getDatapointTime } from '../../../utils/timeseries.js';

export const TimeseriesSelectionAreaTooltip = () => {
  const selectState = useSelectState();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX } = useChartInteractions();
  const timeseriesSelect = useTimeseriesSelect();
  const { currentScale: xScale } = useXScales();
  const zoomToSelection = useZoomToSelection();
  const { data, allDatapoints, datapointsMap } = useTimeseriesFilteredData();
  const { width } = useGridPlotSize();
  const intl = useIntl();
  const chartRef = useTimeseriesOverlayRef();
  const chartBounds = usePointerAndScrollResponsiveChartBounds(
    chartRef,
    true,
  )();

  if (!selectState || !chartBounds) {
    return null;
  }

  const selectionStart = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[0]) / 100
    : selectState.start.relative.x;
  const selectionEnd = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[1]) / 100
    : selectState.end.relative.x;

  const range: [Date, Date] = [
    xScale.invert(selectionStart),
    xScale.invert(selectionEnd),
  ];

  const selectedDatapoints = getSelectedDatapoints(
    allDatapoints,
    datapointsMap,
    range,
  );

  let totalSeries = 0;
  let totalDatapoints = 0;

  const [from, to] = range;

  for (const series of data) {
    let isSeriesPresent = false;

    for (const datapoint of series.datapoints) {
      const datapointDate = new Date(
        getDatapointTime(datapoint.start, datapoint.end),
      );
      if (isDatapointWithinSelection(datapointDate, from, to)) {
        totalDatapoints++;

        isSeriesPresent = true;
      }
    }

    if (isSeriesPresent) {
      totalSeries++;
    }
  }

  const datapointsMessage = getDatapointsMessage(totalDatapoints, data.length);

  const tooltipPosition = getSelectionTooltipPosition(selectState, chartBounds);

  return (
    <SelectionTooltipWithAsyncActions
      actions={() => {
        const actions = timeseriesSelect?.actions;
        return (
          actions &&
          (async () =>
            actions(
              convertToTimeseriesArray(selectedDatapoints),
              range,
            ))().then(getCustomContextMenuItems)
        );
      }}
      selectFinished={selectState.finished}
      header={
        <OverlayTooltip.Header
          header1={[from, to]}
          showRangeDuration1
          subheader={intl.formatMessage(datapointsMessage, {
            count: totalDatapoints,
            seriesCount: totalSeries,
          })}
        />
      }
      position={tooltipPosition}
      zoomEnabled={!!zoomX?.enabled}
      range={selectState.selectionPercentage || [0, 0]}
    >
      {zoomX?.enabled ? (
        <OverlayTooltip.Action
          label={intl.formatMessage(TIMESERIES_MESSAGES.zoomToSelection)}
          icon={<ZoomToSelectionIcon />}
          onSelect={zoomToSelection}
          trailingIcon="⏎"
        />
      ) : null}
    </SelectionTooltipWithAsyncActions>
  );
};
